import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Star, MapPin, Euro, Clock, Heart, Grid, List, SlidersHorizontal, ChevronDown, User, Award, MessageCircle, Calendar, ArrowRight, ArrowLeft, Plus, Package, Briefcase, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import BottomNavigation from '@/components/BottomNavigation';
import LoadingSpinner, { CardSkeleton } from '@/components/LoadingSpinner';
import { NotificationBell } from '@/components/NotificationSystem';

// Mock data
const mockServices = [
  {
    id: 1,
    title: "Marketing Strategy Consulting",
    provider: "Sarah van Dijk",
    category: "Business & Consulting",
    price: "€125",
    priceType: "per uur",
    rating: 4.9,
    reviews: 34,
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Brussel",
    description: "Boost je bedrijf met professionele marketing strategieën en groeiplannen op maat.",
    tags: ["Marketing", "Strategie", "Online"]
  },
  {
    id: 2,
    title: "Professional Photography",
    provider: "Luna Fotografie",
    category: "Creative Services",
    price: "€200",
    priceType: "per shoot",
    rating: 5.0,
    reviews: 67,
    image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Antwerpen",
    description: "Professionele portret- en bedrijfsfotografie die jouw persoonlijkheid en merk laat stralen.",
    tags: ["Fotografie", "Portretten", "Business"]
  },
  {
    id: 3,
    title: "Website Development",
    provider: "Maya Tech Solutions",
    category: "Tech & Digital",
    price: "€75",
    priceType: "per uur",
    rating: 4.8,
    reviews: 42,
    image: "https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Gent",
    description: "Moderne, responsive websites die converteren. Van concept tot live website.",
    tags: ["Webdesign", "Development", "E-commerce"]
  }
];

const mockProducts = [
  {
    id: 101,
    title: "Marketing Mastery E-book",
    provider: "Sarah van Dijk",
    category: "Digitale Producten",
    price: "€29",
    originalPrice: "€39",
    rating: 4.9,
    reviews: 156,
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?auto=compress&cs=tinysrgb&w=400",
    deliveryTime: "Direct download",
    description: "Complete gids voor succesvolle marketing strategieën. 150+ pagina's met praktische tips.",
    tags: ["E-book", "Marketing", "PDF"]
  },
  {
    id: 102,
    title: "Logo Design Pakket",
    provider: "Nina Design Studio",
    category: "Creative Producten",
    price: "€150",
    rating: 5.0,
    reviews: 43,
    image: "https://images.pexels.com/photos/196645/pexels-photo-196645.jpeg?auto=compress&cs=tinysrgb&w=400",
    deliveryTime: "3-5 werkdagen",
    description: "Professioneel logo design met 3 concepten, onbeperkte revisies en alle bestandsformaten.",
    tags: ["Logo", "Branding", "Custom"]
  }
];

const mockJobs = [
  {
    id: 401,
    title: "Mindfulness & Wellness Coach",
    company: "Conscious Living Collective",
    category: "Coaching & Wellness",
    salary: "€35-45",
    salaryType: "per uur",
    workType: "Part-time",
    location: "Amsterdam",
    locationType: "Hybrid",
    rating: 4.8,
    reviews: 12,
    image: "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Werk als mindfulness coach in een bewuste community. Begeleid vrouwen op hun wellness journey.",
    tags: ["Mindfulness", "Coaching", "Wellness"]
  },
  {
    id: 402,
    title: "Creative Content Manager",
    company: "Women's Empowerment Hub",
    category: "Marketing & Content",
    salary: "€3.500-4.200",
    salaryType: "per maand",
    workType: "Full-time",
    location: "Brussels",
    locationType: "Hybrid",
    rating: 4.9,
    reviews: 28,
    image: "https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&w=400",
    description: "Leid het content team en creëer inspirerende verhalen die vrouwen empoweren.",
    tags: ["Content", "Marketing", "Social Media"]
  }
];

const MarketplaceFixed: React.FC = () => {
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState<'browse' | 'mijn-aanbod'>('browse');
  const [activeBrowseTab, setActiveBrowseTab] = useState<'services' | 'products' | 'jobs'>('services');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Get current data based on active tab
  const currentData = useMemo(() => {
    switch (activeBrowseTab) {
      case 'services':
        return mockServices;
      case 'products':
        return mockProducts;
      case 'jobs':
        return mockJobs;
      default:
        return mockServices;
    }
  }, [activeBrowseTab]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchQuery) return currentData;
    return currentData.filter(item =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ('provider' in item && item.provider.toLowerCase().includes(searchQuery.toLowerCase())) ||
      ('company' in item && item.company.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [currentData, searchQuery]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-elegant pb-24">
        <div className="sticky top-0 z-50 w-full bg-white border-b border-cream-300 shadow-sm">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="skeleton h-8 w-8 rounded-full" />
                <div>
                  <div className="skeleton-title h-6 w-32" />
                  <div className="skeleton-text h-4 w-48" />
                </div>
              </div>
              <div className="skeleton h-8 w-8 rounded-full" />
            </div>
            <div className="skeleton h-12 w-80 mx-auto rounded-lg mb-6" />
            <div className="skeleton h-10 w-full rounded-lg" />
          </div>
        </div>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-elegant pb-24">
        {/* Header */}
        <div className="sticky top-0 z-50 w-full bg-white border-b border-cream-300 shadow-sm">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-4">
            {/* Header Row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-cream-100 flex-shrink-0 text-navy-600"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl font-semibold truncate text-navy-700">WOM Plaza</h1>
                  <p className="text-xs sm:text-sm truncate text-navy-500">
                    Diensten, producten & vacatures van vrouwen in België
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <NotificationBell />
              </div>
            </div>

            {/* WOM Plaza Header */}
            <div className="rounded-lg p-4 mb-6 bg-cream-200 border border-cream-300">
              <div className="text-center">
                <h1 className="text-2xl sm:text-3xl font-light tracking-wider text-navy-700 mb-2">
                  <span className="font-bold text-navy-800">W</span>
                  <span className="mx-1 text-gold-400">·</span>
                  <span className="font-bold text-navy-800">O</span>
                  <span className="mx-1 text-gold-400">·</span>
                  <span className="font-bold text-navy-800">M</span>
                  <span className="ml-3 font-bold text-navy-800">Plaza</span>
                </h1>
                <p className="text-sm text-navy-500">
                  Ontdek diensten, producten en vacatures van vrouwen in België
                </p>
              </div>
            </div>

            {/* Main Navigation Tabs */}
            <div className="w-full overflow-x-auto scrollbar-hide mb-4">
              <div className="flex items-center gap-1 rounded-lg p-1 min-w-max w-fit mx-auto bg-cream-200">
                <button
                  onClick={() => setActiveMainTab('browse')}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                    activeMainTab === 'browse' 
                      ? 'bg-white shadow-sm text-navy-700' 
                      : 'hover:bg-white/50 text-navy-500'
                  }`}
                >
                  <Search className="h-4 w-4" />
                  <span className="hidden sm:inline">Ontdekken</span>
                  <span className="sm:hidden">Zoeken</span>
                </button>
                <button
                  onClick={() => setActiveMainTab('mijn-aanbod')}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm sm:text-base whitespace-nowrap ${
                    activeMainTab === 'mijn-aanbod' 
                      ? 'bg-white shadow-sm text-navy-700' 
                      : 'hover:bg-white/50 text-navy-500'
                  }`}
                >
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Mijn Aanbod</span>
                  <span className="sm:hidden">Mijn</span>
                </button>
              </div>
            </div>

            {/* Browse Tab Content */}
            {activeMainTab === 'browse' && (
              <>
                {/* Services/Products/Jobs Toggle */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                  <div className="w-full sm:w-auto overflow-x-auto scrollbar-hide">
                    <div className="flex items-center gap-1 rounded-lg p-1 min-w-max bg-cream-200">
                      <button
                        onClick={() => setActiveBrowseTab('services')}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                          activeBrowseTab === 'services' 
                            ? 'bg-white shadow-sm text-navy-700' 
                            : 'hover:bg-white/50 text-navy-500'
                        }`}
                      >
                        <Clock className="h-4 w-4" />
                        Diensten
                      </button>
                      <button
                        onClick={() => setActiveBrowseTab('products')}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                          activeBrowseTab === 'products' 
                            ? 'bg-white shadow-sm text-navy-700' 
                            : 'hover:bg-white/50 text-navy-500'
                        }`}
                      >
                        <Package className="h-4 w-4" />
                        Producten
                      </button>
                      <button
                        onClick={() => setActiveBrowseTab('jobs')}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap ${
                          activeBrowseTab === 'jobs' 
                            ? 'bg-white shadow-sm text-navy-700' 
                            : 'hover:bg-white/50 text-navy-500'
                        }`}
                      >
                        <Briefcase className="h-4 w-4" />
                        Vacatures
                      </button>
                    </div>
                  </div>
                  {activeBrowseTab === 'services' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto hover:bg-cream-100 text-xs sm:text-sm border-cream-300 text-navy-600"
                    >
                      <Map className="h-4 w-4 mr-2" />
                      Kaart Weergave
                    </Button>
                  )}
                </div>

                {/* Search */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-navy-400 h-4 w-4" />
                  <Input
                    placeholder={`Zoek in ${activeBrowseTab === 'services' ? 'diensten' : activeBrowseTab === 'products' ? 'producten' : 'vacatures'}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {/* Browse Content */}
          {activeMainTab === 'browse' && (
            <>
              {filteredData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mb-6">
                    <div 
                      className="h-20 w-20 mx-auto rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#efe7dd' }}
                    >
                      <Search className="h-8 w-8" style={{ color: '#a0897c' }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-navy-700">
                    Geen {activeBrowseTab === 'services' ? 'diensten' : 
                          activeBrowseTab === 'products' ? 'producten' : 'vacatures'} gevonden
                  </h3>
                  <p className="text-base mb-6 max-w-lg mx-auto text-navy-500">
                    Probeer je zoekopdracht aan te passen of bekijk andere categorieën.
                  </p>
                  <Button 
                    onClick={() => setSearchQuery('')}
                    className="bg-navy-700 hover:bg-navy-800 text-white"
                  >
                    Wis zoekopdracht
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {filteredData.map(item => (
                    <Card 
                      key={item.id}
                      className="overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-cream-300"
                      onClick={() => navigate(
                        activeBrowseTab === 'services' ? `/service/${item.id}` : 
                        activeBrowseTab === 'products' ? `/product/${item.id}` : 
                        `/job/${item.id}`
                      )}
                    >
                      <div className="aspect-video bg-cover bg-center relative overflow-hidden">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          width={400}
                          height={225}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                        {item.rating >= 4.8 && (
                          <div className="absolute top-3 left-3">
                            <Badge className="text-white text-xs bg-navy-700">
                              Top Rated
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <h3 className="font-medium text-base line-clamp-2 flex-1 text-navy-700">{item.title}</h3>
                          <div className="text-right flex-shrink-0">
                            {activeBrowseTab === 'jobs' && 'salary' in item ? (
                              <>
                                <div className="font-semibold text-sm text-navy-800">{item.salary}</div>
                                <div className="text-xs text-navy-500">{item.salaryType}</div>
                              </>
                            ) : (
                              <>
                                <div className="font-semibold text-sm text-navy-800">{item.price}</div>
                                {'originalPrice' in item && item.originalPrice && (
                                  <div className="text-xs line-through text-navy-400">{item.originalPrice}</div>
                                )}
                                {'priceType' in item && (
                                  <div className="text-xs text-navy-500">{item.priceType}</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        <p className="text-sm mb-3 line-clamp-2 text-navy-500">
                          {item.description}
                        </p>

                        <div className="flex items-center justify-between mb-3 gap-2">
                          <div className="text-sm font-medium truncate flex-1 text-navy-700">
                            {activeBrowseTab === 'jobs' && 'company' in item ? item.company : 
                             'provider' in item ? item.provider : ''}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{item.rating}</span>
                            <span className="text-xs text-navy-500">({item.reviews})</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {activeBrowseTab === 'jobs' && 'location' in item ? (
                              <div className="flex items-center gap-1 text-xs text-navy-500">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{item.location}</span>
                                {'locationType' in item && (
                                  <>
                                    <span className="mx-1">•</span>
                                    <span className="truncate">{item.locationType}</span>
                                  </>
                                )}
                              </div>
                            ) : activeBrowseTab === 'services' && 'location' in item ? (
                              <div className="flex items-center gap-1 text-xs text-navy-500">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{item.location}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-xs text-navy-500">
                                <Package className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{'deliveryTime' in item && item.deliveryTime}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {item.tags.slice(0, 2).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs bg-cream-200 text-navy-700">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Mijn Aanbod Content */}
          {activeMainTab === 'mijn-aanbod' && (
            <div className="text-center py-12">
              <div className="mb-6">
                <div 
                  className="h-20 w-20 mx-auto rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#efe7dd' }}
                >
                  <User className="h-8 w-8" style={{ color: '#a0897c' }} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-700">
                Mijn Aanbod
              </h3>
              <p className="text-base mb-6 max-w-lg mx-auto text-navy-500">
                Hier kun je je diensten en producten beheren. Ga naar je profiel om ze toe te voegen!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/profiel')}
                  className="bg-navy-700 hover:bg-navy-800 text-white"
                >
                  <User className="h-4 w-4 mr-2" />
                  Ga naar Profiel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <BottomNavigation />
      </div>
    </>
  );
};

export default MarketplaceFixed;